import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
// Removed config import to avoid production issues
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Target, 
  TrendingUp, 
  Shield, 
  Users, 
  BookOpen,
  Award,
  Lightbulb,
  Building,
  GraduationCap
} from "lucide-react";

const KIBeratung = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // SEO Configuration
  const seoConfig = {
    title: "KI-Akademie - C-Level Coaching Künstliche Intelligenz | BROVKO",
    description: "Maßgeschneiderte KI-Qualifizierung für nachhaltige Transformation. C-Level Coaching, Executive Training und KI-Schulungen für Unternehmen. Jetzt KI-Academy buchen!",
    keywords: "C-Level Coaching Künstliche Intelligenz, KI-Akademie, Executive KI-Training, KI-Schulungen für Unternehmen, AI Compliance Training, KI-Qualifizierung, Change Management KI",
    canonicalUrl: "/leistungsangebot/ki-akademie",
    ogTitle: "KI-Akademie - C-Level Coaching & Executive KI-Training | BROVKO",
    ogDescription: "Führende KI-Akademie für nachhaltige Transformation. C-Level Coaching, Compliance-Training und maßgeschneiderte KI-Qualifizierung für Ihr Unternehmen.",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "BROVKO KI-Akademie",
      "description": "Maßgeschneiderte KI-Qualifizierung für nachhaltige Transformation",
      "url": "/leistungsangebot/ki-akademie",
      "provider": {
        "@type": "Organization",
        "name": "BROVKO"
      },
      "courseMode": ["Blended", "InPerson", "Online"],
      "educationalCredentialAwarded": "KI-Zertifizierung",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "KI-Akademie Programme",
        "itemListElement": [
          {
            "@type": "Course",
            "name": "Executive KI-Coaching",
            "description": "Individuelle Programme für Top-Führungskräfte"
          },
          {
            "@type": "Course",
            "name": "AI Compliance & Governance Trainings",
            "description": "Sicher und regelkonform nach EU AI Act"
          },
          {
            "@type": "Course",
            "name": "C-Level Coaching Künstliche Intelligenz",
            "description": "Strategisches KI-Coaching für Führungskräfte"
          }
        ]
      }
    }
  };

  // Modal handlers
  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const whyBrovko = [
    "Einzigartige Basis – das Fundament für jede erfolgreiche KI-Transformation",
    "C-Level Coaching & CAIO-Ausbildung – Führung im KI-Zeitalter stärken",
    "On-the-Project Enablement – Wissenstransfer während laufender Projekte",
    "Praxisorientierte Trainings – von Prompting bis Microsoft Copilot",
    "Maßgeschneiderte KI-Kurse – individuelle Lernpfade für Ihr Unternehmen"
  ];

  const corePrograms = [
    {
      icon: Users,
      title: "Executive KI-Coaching",
      subtitle: "Individuelle Programme für Top-Führungskräfte",
      features: [
        "Coaching für Vorstände, CxOs & Aufsichtsräte",
        "On-the-job CAIO-Ausbildung",
        "Strategisches KI-Wissen: Chancen, Risiken & Governance"
      ]
    },
    {
      icon: Shield,
      title: "AI Compliance & Governance Trainings",
      subtitle: "Sicher und regelkonform nach EU AI Act",
      features: [
        "Grundlagen & Pflichten des EU AI Acts",
        "Aufbau interner Governance-Strukturen",
        "Risiko- und Compliance-Checks für Ihre KI-Systeme"
      ]
    },
    {
      icon: TrendingUp,
      title: "Promotoren-Programme & Change Management",
      subtitle: "Interne KI-Multiplikatoren entwickeln",
      features: [
        "Ausbildung von KI-Promotoren in Fachbereichen",
        "Begleitung von Change-Prozessen & Kommunikation",
        "Kulturwandel nachhaltig verankern"
      ]
    },
    {
      icon: BookOpen,
      title: "Hands-on Trainings",
      subtitle: "Praktische Fähigkeiten für den Alltag",
      features: [
        "Prompting-Schulungen für LLMs",
        "Microsoft Copilot-Trainings (Basis-Qualifizierung)",
        "Bootcamps & Workshops für schnelle Erfolge"
      ]
    }
  ];

  const additionalFormats = [
    {
      icon: Award,
      title: "Individuelle KI-Kurse",
      subtitle: "Maßgeschneiderte Lernpfade für Ihr Unternehmen",
      features: [
        "Entwicklung eigener Kursinhalte für Fachbereiche",
        "Kombination aus Präsenz, E-Learning & Projektarbeit",
        "Zertifizierte Lernpfade für nachhaltige Kompetenz"
      ]
    },
    {
      icon: Target,
      title: "On-the-Project Enablement",
      subtitle: "Wissenstransfer während laufender Projekte",
      features: [
        "Kontinuierliches Lernen im Rahmen Ihrer Projekte",
        "Direkter Aufbau von Kompetenzen im Team",
        "Learning by Doing für schnelle Wirkung"
      ]
    },
    {
      icon: GraduationCap,
      title: "AI Bootcamps & Intensivworkshops",
      subtitle: "Kompakte Formate für schnellen Kompetenzaufbau",
      features: [
        "2–3-tägige Intensivprogramme",
        "Praxisübungen an echten Use Cases",
        "Sofort nutzbares Wissen für Ihre Teams"
      ]
    }
  ];

  const supplementaryServices = [
    {
      title: "KI-Strategie-Workshops",
      subtitle: "Individuelle Ausrichtung für Ihr Unternehmen",
      features: [
        "Entwicklung maßgeschneiderter KI-Zielbilder",
        "Ableitung von Prioritäten und Use Cases",
        "Erstellung einer Roadmap für den Start"
      ]
    },
    {
      title: "KI-Skalierungs-Workshops",
      subtitle: "Von Pilotprojekten zur breiten Anwendung",
      features: [
        "Methoden zur Skalierung erfolgreicher KI-Projekte",
        "Best Practices für Organisation, Architektur & Governance",
        "Aufbau nachhaltiger KI-Exzellenzprogramme"
      ]
    },
    {
      title: "KI-Potenzial-Analyzer-Workshops",
      subtitle: "Die wertvollsten Einsatzfelder identifizieren",
      features: [
        "Analyse von Daten, Prozessen und Marktchancen",
        "Priorisierung von Anwendungsfällen nach Nutzen & Machbarkeit",
        "Entwicklung eines Maßnahmenplans mit klaren KPIs"
      ]
    },
    {
      title: "KI-Use-Case-Factory-Workshops",
      subtitle: "Von der Idee zum konkreten Anwendungsfall",
      features: [
        "Strukturierte Entwicklung neuer KI-Use Cases",
        "Einsatz von Templates, Checklisten und Benchmark-Cases",
        "Ergebnis: umsetzbare Projekt-Blueprints für Ihr Unternehmen"
      ]
    }
  ];

  const methodology = [
    "Individuelle Lernpfade – zugeschnitten auf Rollen & Branchen",
    "On-the-Project Training – Lernen im Projektalltag",
    "Best Practices & Live-Demos – Lernen aus hunderten Use Cases",
    "Digitale Lernplattformen – adaptive Inhalte & Checklisten",
    "Messbare Wirkung – klare KPIs zur Kompetenzentwicklung"
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
              Maßgeschneiderte KI-Qualifizierung für nachhaltige Transformation
            </h1>
            <div className="text-base text-ink-muted mb-6 leading-relaxed space-y-3">
              <p>
                Die erfolgreiche Nutzung von Künstlicher Intelligenz (KI) erfordert mehr als Technologie. 
                Unternehmen brauchen Führungskräfte, Mitarbeitende und Promotoren mit den richtigen Fähigkeiten. 
                Mit der BROVKO AI Academy schaffen Sie die einzigartige Basis für eine erfolgreiche KI-Transformation.
              </p>
              <p>
                Wir verbinden C-Level-Coaching, praxisnahe Trainings, regulatorische Sicherheit und on-the-project 
                Knowledge Transfer. So stellen wir sicher, dass Know-how direkt in die Organisation hineinwächst – 
                und dauerhaft verfügbar bleibt.
              </p>
            </div>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
              onClick={() => openContactModal("Hero Section", "Training buchen")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Training buchen
            </Button>
          </div>
        </div>
      </section>

      {/* Why Brovko Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              Warum BROVKO AI Academy?
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center">
              Einzigartige Basis – das Fundament für jede erfolgreiche KI-Transformation
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {whyBrovko.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-brand-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-ink-muted leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Training Programs */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              Unsere Kernschulungsprogramme
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {corePrograms.map((program, index) => {
                const IconComponent = program.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{program.title}</h3>
                        <p className="text-ink-faint text-xs mt-0.5">{program.subtitle}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {program.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand"
                      onClick={() => openContactModal("Kernschulungsprogramme", "Training buchen")}
                    >
                      Training buchen
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Training Formats */}
      <section className="py-20 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              Weitere Trainingsformate
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {additionalFormats.map((format, index) => {
                const IconComponent = format.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-6 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white">{format.title}</h3>
                    </div>
                    <p className="text-ink-faint text-sm mb-4">{format.subtitle}</p>
                    <ul className="space-y-2 mb-4">
                      {format.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-sm leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand w-full"
                      onClick={() => openContactModal("Weitere Trainingsformate", "Training buchen")}
                    >
                      Training buchen
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Supplementary Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              Ergänzende Leistungen der BROVKO AI Academy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {supplementaryServices.map((service, index) => (
                <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-6 border border-hairline">
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-2">{service.title}</h3>
                  <p className="text-ink-faint text-sm mb-4">{service.subtitle}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-muted text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-8 text-center">
              Unsere Methodik
            </h2>
            <p className="text-xl text-ink-faint mb-12 text-center">
              Wir kombinieren Didaktik, Praxis und Transformationserfahrung:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {methodology.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-brand-400 mt-1 flex-shrink-0" />
                  <p className="text-ink-muted leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
              Jetzt Ihre BROVKO AI Academy starten!
            </h2>
            <p className="text-xl text-ink-muted mb-12">
              Legen Sie mit der BROVKO AI Academy die Grundlage für Ihre erfolgreiche KI-Transformation – 
              praxisnah, regulatorisch sicher und nachhaltig.
            </p>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-8 py-4 text-lg font-semibold"
              onClick={() => openContactModal("Final CTA", "Training buchen")}
            >
              <Calendar className="mr-2 h-5 w-5" />
              Training buchen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Akademie"
        buttonContext="KI-Akademie Training"
        buttonText="Training buchen"
      />
    </div>
  );
};

export default KIBeratung;